/* eslint-disable react/destructuring-assignment */
import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import { useBreakpoint } from '@next-common/breakpoint';
import { debounce } from '../../../../shared/functionUtils';
import { isAboveLandscape } from '../../../../shared/breakpoints';
import { hideAll as hideAllTooltips } from '../../../Tooltip';
import Geoloc from '../GeolocCtn';
import Suggestion from './components/Suggestion/suggestion';
import searchUtils from './searchUtils';

// Constantes
import { computeSearchString } from '../../../../shared/string';

// icons
import ZoomIcon from '../../../../svg/utils/zoom';
import ChevronLeftIcon from '../../../../svg/nav/chevronLeftIcon';

// Style
import t from '../../../../css/global.module.css';
import s from './search.module.css';
import theme from './autocomplete.module.css';

import domainApi from '../../../../shared/domainApi';
import glossaryTagQA from '../../../../tracking/qa/glossaryTagQA';
import QaTracker from '../../../../tracking/qa/qaTracker';
import GenericService from '../../../../modules/common/services/genericService';


const cache = {};

const getData = (keywordInput, category, cb) => {
  const path = `/restpublic/tech-web/searchAutoComplete/${keywordInput}`;
  const host = GenericService.cleanEndPoint(domainApi());
  const url = `${host}${path}`;
  return fetch(url)
    .then((response) => {
      cache[keywordInput] = [];
      return response
        .json()
        .then((data) => {
          if (response.status !== 200 || data.errors) {
            return [];
          }
          cache[keywordInput] = data;
          return data;
        })
        .catch(() => ([]));
    })
    .catch(() => ([]))
    .then(data => (cb(data)));
};

const debounceData = debounce(getData, 50);

const getSuggestions = (value, category) => new Promise((resolve) => {
  if (cache[value]) {
    return resolve(cache[value]);
  }
  return debounceData(value, category.value, resolve);
});

function shouldRenderSuggestions(value) {
  return value.trim().length > 0;
}

class Search extends React.Component {
  constructor(props, context) {
    let value = '';
    if (typeof window !== 'undefined' && window.INPUTSEARCHVALUE) {
      value = window.INPUTSEARCHVALUE;
    }
    super(props, context);
    this.state = {
      category: { value: null, label: null },
      isOpen: false,
      isFocused: false,
      suggestions: [],
      suggestion: {},
      value,
    };
    this.setOutline = this.setOutline.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(
      this,
    );
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(
      this,
    );
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.toggleGeolocGlobalState = this.toggleGeolocGlobalState.bind(this);
    this.loop = React.createRef();
  }

  componentDidMount() {
    if (this.props.persistedSearch) {
      this.setState((state, { persistedSearch }) => ({ ...state, value: persistedSearch }));
    }
    if (this.state.value !== '') {
      document.querySelector('#header #PageForm input').focus();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isNav && prevProps.persistedSearch !== this.props.persistedSearch) {
      this.setState((state, { persistedSearch }) => ({ ...state, value: persistedSearch })); /* eslint-disable-line react/no-did-update-set-state */
    }
    if (this.props.isNav === true && prevProps.isNav === false && this.props.persistedSearch) {
      this.setState(state => ({ ...state, value: '' })); /* eslint-disable-line react/no-did-update-set-state */
    }
  }

  onChange(event, { newValue }) {
    this.setState({
      value: newValue,
    });
  }

  onSubmit(suggestion, category) {
    const categoryValue = suggestion.category
      ? suggestion.category.value
      : category.value;
    // if there is no search term given we don't change the url
    if (searchUtils.isInvalidInput(suggestion.value, categoryValue)) {
      return null;
    }

    const { internalSearchIsActive } = this.props;
    const searchBaseUrl = internalSearchIsActive ? 'search' : 's';
    const url = `/${searchBaseUrl}/${computeSearchString(suggestion.value)}${
      categoryValue ? `?nav=${categoryValue}` : ''
    }`;

    let searchUrl = this.state.geolocEnabled ? searchUtils.addGeolocParameter(url) : url;
    searchUrl = searchUtils.checkForFilterKeyword(searchUrl);
    if (this.context.storeHelper && this.context.storeHelper.hasSearch(searchUrl)) {
      this.context.storeHelper.search(searchUrl);
    } else {
      window.location = searchUrl;
    }
    return null;
  }

  // Ajax callback
  onSuggestionsFetchRequested({ value }) {
    getSuggestions(value, this.state.category).then((suggestionsData) => {
      const { breakpoint } = this.props;
      const isMobile = !isAboveLandscape(breakpoint);
      const suggestions = suggestionsData.reduce((res, suggestion) => {
        if (!suggestion.categories || isMobile) {
          res.push({
            keyword: suggestion.keyword,
            value: suggestion.value,
            category: {
              label: null,
              value: null,
            },
            resultCount: suggestion.resultCount,
          });
        }
        if (suggestion.categories) {
          suggestion.categories.forEach(category => res.push({
            keyword: suggestion.keyword,
            category: {
              label: category.label,
              value: category.value,
            },
            value: suggestion.value,
            resultCount: category.resultCount,
            isMobile,
            isLastCategory: suggestion.categories[suggestion.categories.length - 1] === category,
          }));
        }
        return res;
      }, []);

      this.setState({ suggestions, isOpen: true });
      hideAllTooltips();
    });
  }

  onKeyDown(event) {
    if (event.key === 'Enter') {
      const { suggestion } = this.state;
      suggestion.value = suggestion.value || this.state.value;
      // loose focus
      event.preventDefault();
      this.focus();

      // document.body.focus();
      setTimeout(() => this.onSubmit(suggestion, this.state.category), 0);
    } else {
      this.setState({
        isFocused: true,
        isOpen: true,
      });
      this.props.handlerFocus(true);
    }
  }

  onSuggestionSelected(event, { method, suggestion }) {
    if (method === 'click') {
      setTimeout(() => {
        this.onSubmit(suggestion, this.state.category);
      }, 0);
    }
  }

  // When erase requests
  onSuggestionsClearRequested() {
    this.setState({
      suggestions: [],
      suggestion: {},
    });
    // For the front behavior when small
    // this.props.handlerFocus(false);
  }

  setOutline(status) {
    this.setState({
      isFocused: status,
      isOpen: status,

    });
    this.props.handlerFocus(status);
    if (this.context.storeHelper) {
      this.context.storeHelper.setFocus(status);
    }
  }


  focus() {
    if (this.loop) {
      this.loop.current.focus();
    }
  }

  toggleGeolocGlobalState(enable = false) {
    this.setState({ geolocEnabled: enable });
  }


  render() {
    const { placeholders, location } = this.props;

    const inputProps = {
      value: this.state.value,
      onChange: this.onChange,
      onKeyDown: this.onKeyDown,
      name: 'searchField',
      id: 'searchField',
    };

    const getSuggestionValue = (suggestion) => {
      this.setState({
        suggestion,
      });
      return suggestion.keyword;
    };

    /*
      For visual debug :
      Commenter la props > onSuggestionsClearRequested={this.onSuggestionsClearRequested}
      Ajouter la props > alwaysRenderSuggestions
     */
    return (
      <div
        onFocus={() => this.setOutline(true)}
        onBlur={() => this.setOutline(false)}
        className={`${t.flex} ${t.br3}
        ${this.state.suggestions.length > 1 ? s.suggested : ''}
        ${!this.state.isFocused ? s.defaultBorder : null}
        ${this.state.isFocused ? s.focused : ''}
        ${s.wrapper}
        ${s.flex_grow}
        ${s.mr_search}`}
      >
        {this.state.isOpen && (
          <button
            type="button"
            className={`${t.white} ${t.bn} ${t.br2} ${s.brLeft} ${t['bg-white']} ${t['helper_show-BelowDesktop']} ${s.button}`}
            onClick={this.onSuggestionsClearRequested}
          >
            <span className={s.buttonIcon}>
              <ChevronLeftIcon />
            </span>
          </button>
        )}
        <form
          id="PageForm"
          action="/s"
          className={`${t.flex} ${t['helper_flex-occupy']} ${s.search} ${t.relative}`}
        >
          <QaTracker customClass={`${t.flex} ${t['helper_flex-occupy']} ${t.relative}`} tagName={glossaryTagQA.header.search.input}>
            <Autosuggest
              suggestions={this.state.suggestions}
              theme={theme}
              onSuggestionSelected={this.onSuggestionSelected}
              onSuggestionsClearRequested={this.onSuggestionsClearRequested}
              onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
              getSuggestionValue={getSuggestionValue}
              renderSuggestion={Suggestion}
              inputProps={inputProps}
              shouldRenderSuggestions={shouldRenderSuggestions}
              focusInputOnSuggestionClick={false}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label
              htmlFor="searchField"
              className={`${theme.placeholderLabelAboveLandscape}  ${theme.placeholderLabel} ${this.state.value !== '' ? theme.placeholderLabelHiddenWithText : ''}`}
            >
              {placeholders.large}
            </label>
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label
              htmlFor="searchField"
              className={`${theme.placeholderLabelBelowLandscape} ${theme.placeholderLabel} ${this.state.value !== '' ? theme.placeholderLabelHiddenWithText : ''}`}
            >
              {placeholders.small}
            </label>

          </QaTracker>
          <Geoloc
            labels={location}
            updateGlobalGeolocState={this.toggleGeolocGlobalState}
          />
        </form>
        <button
          type="button"
          className={`${t['bg-mid-gray']} ${t.pointer} ${t.flex} ${t.white} ${t.br2} ${t.bn} ${s.brRight} ${s.button}`}
          ref={this.loop}
          data-qa={glossaryTagQA.header.search.button}
          onClick={() => this.onSubmit(
            this.state.suggestion.value
              ? this.state.suggestion
              : { value: this.state.value },
            this.state.category,
          )
          }
        >
          <span className={s.buttonIcon}>
            <ZoomIcon fill="#ffffff" />
          </span>
        </button>
      </div>
    );
  }
}

Search.propTypes = {
  placeholders: PropTypes.objectOf(PropTypes.string).isRequired,
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ).isRequired,
  breakpoint: PropTypes.string.isRequired,
  handlerFocus: PropTypes.func,
  internalSearchIsActive: PropTypes.bool,
  persistedSearch: PropTypes.string,
  isNav: PropTypes.bool,
};

Search.defaultProps = {
  handlerFocus: () => {},
  internalSearchIsActive: false,
  persistedSearch: null,
  isNav: false,
};

Search.contextTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  storeHelper: PropTypes.object,
};


// Fix to not mix react 15 context (storehelper) and react 16 context (breakpoint) in 1 component, this is not possible
const SearchWrapperBreakpointContextToProps = (props) => {
  const { breakpoint } = useBreakpoint();
  return (<Search breakpoint={breakpoint} {...props} />);
};
export default SearchWrapperBreakpointContextToProps;
